import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  X,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { clientsAPI } from '../services/api';

interface Client {
  _id: string;
  clientName: string;
  contactNumber: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  taxType?: 'IGST' | 'SGST_CGST';
  placeOfSupply: string;
  isActive: boolean;
  createdAt: string;
}

interface ClientFormData {
  clientName: string;
  contactNumber: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin: string;
  placeOfSupply: string;
}

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [doesNotHaveGST, setDoesNotHaveGST] = useState(false);

  // Create schema with dynamic GSTIN validation
  const schema = useMemo(() => {
    return yup.object({
      clientName: yup.string().required('Client name is required'),
      contactNumber: yup.string().test('mobile', 'Invalid mobile number', (value) => {
        if (!value || value.length === 0) return true;
        return /^[6-9]\d{9}$/.test(value);
      }),
      email: yup.string().required('Email is required').email('Invalid email format'),
      address: yup.object({
        street: yup.string().required('Street address is required'),
        city: yup.string().required('City is required'),
        state: yup.string().required('State is required'),
        pincode: yup.string().required('Pincode is required').matches(/^[1-9][0-9]{5}$/, 'Invalid pincode'),
        country: yup.string().required('Country is required'),
      }),
      gstin: doesNotHaveGST 
        ? yup.string().optional()
        : yup.string().matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN format'),
      placeOfSupply: yup.string().required('Place of supply is required'),
    });
  }, [doesNotHaveGST]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      address: {
        country: 'India'
      }
    }
  });

  // Watch pincode for auto-fill
  const pincode = watch('address.pincode' as any);

  const fetchClients = useCallback(async () => {
    try {
      const response = await clientsAPI.getAll({ search: searchTerm });
      setClients(response.data.clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setMessage({ type: 'error', text: 'Failed to fetch clients' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== '') {
        fetchClients();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchClients]);

  // Auto-fill city and state based on pin code
  useEffect(() => {
    const fetchLocationData = async () => {
      if (pincode && pincode.length === 6 && /^[1-9][0-9]{5}$/.test(pincode)) {
        try {
          const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
          const data = await response.json();
          
          if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
            const postOffice = data[0].PostOffice[0];
            setValue('address.city' as any, postOffice.District || '');
            setValue('address.state' as any, postOffice.State || '');
          }
        } catch (error) {
          console.error('Error fetching location data:', error);
        }
      }
    };

    fetchLocationData();
  }, [pincode, setValue]);

  const onSubmit = async (data: any) => {
    setSaving(true);
    setMessage(null);

    try {
      // If checkbox is checked, set GSTIN to empty string
      if (doesNotHaveGST) {
        data.gstin = '';
      }
      
      if (editingClient) {
        await clientsAPI.update(editingClient._id, data);
        setMessage({ type: 'success', text: 'Client updated successfully!' });
      } else {
        await clientsAPI.create(data);
        setMessage({ type: 'success', text: 'Client created successfully!' });
      }
      
      reset();
      setDoesNotHaveGST(false);
      setShowForm(false);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      setMessage({ type: 'error', text: 'Failed to save client' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setValue('clientName' as any, client.clientName);
    setValue('contactNumber' as any, client.contactNumber);
    setValue('email' as any, client.email);
    setValue('address.street' as any, client.address.street);
    setValue('address.city' as any, client.address.city);
    setValue('address.state' as any, client.address.state);
    setValue('address.pincode' as any, client.address.pincode);
    setValue('address.country', client.address.country);
    setValue('gstin' as any, client.gstin || '');
    setValue('placeOfSupply' as any, client.placeOfSupply);
    // Set checkbox state based on whether client has GSTIN
    setDoesNotHaveGST(!client.gstin);
    setShowForm(true);
  };

  const handleDeleteClick = (client: Client) => {
    setDeletingClient(client);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingClient) return;
    
    setDeleting(true);
    setMessage(null);
    
    try {
      await clientsAPI.delete(deletingClient._id);
      setMessage({ type: 'success', text: 'Client deleted successfully!' });
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      setMessage({ type: 'error', text: 'Failed to delete client' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeletingClient(null);
    }
  };

  const handleCancel = () => {
    reset();
    setDoesNotHaveGST(false);
    setShowForm(false);
    setEditingClient(null);
  };

  const handleGSTCheckboxChange = async (checked: boolean) => {
    setDoesNotHaveGST(checked);
    if (checked) {
      setValue('gstin' as any, '');
    }
    // Re-validate GSTIN field after checkbox change
    await trigger('gstin' as any);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
            <p className="text-gray-600">Manage your client database</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Client</span>
        </button>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Client Form Modal */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Client Name *</label>
                    <input
                      type="text"
                      {...register('clientName' as any)}
                      className="form-input"
                      placeholder="Enter client name"
                    />
                    {(errors as any).clientName && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).clientName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Contact Number</label>
                    <input
                      type="tel"
                      {...register('contactNumber' as any)}
                      className="form-input"
                      placeholder="9876543210"
                    />
                    {(errors as any).contactNumber && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).contactNumber.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      {...register('email' as any)}
                      className="form-input"
                      placeholder="client@example.com"
                    />
                    {(errors as any).email && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).email.message}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <input
                        type="checkbox"
                        id="doesNotHaveGST"
                        checked={doesNotHaveGST}
                        onChange={(e) => handleGSTCheckboxChange(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="doesNotHaveGST" className="text-sm text-gray-700 cursor-pointer">
                        Client does not have GST
                      </label>
                    </div>
                    <label className="form-label">GSTIN {!doesNotHaveGST && <span className="text-red-500">*</span>}</label>
                    <input
                      type="text"
                      {...register('gstin' as any)}
                      className={`form-input ${doesNotHaveGST ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder="22AAAAA0000A1Z5"
                      style={{ textTransform: 'uppercase' }}
                      disabled={doesNotHaveGST}
                    />
                    {(errors as any).gstin && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).gstin.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="form-label">Place of Supply *</label>
                    <input
                      type="text"
                      {...register('placeOfSupply' as any)}
                      className="form-input"
                      placeholder="Enter place of supply"
                    />
                    {(errors as any).placeOfSupply && (
                      <p className="mt-1 text-sm text-red-600">{(errors as any).placeOfSupply.message}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Street Address *</label>
                      <input
                        type="text"
                        {...register('address.street' as any)}
                        className="form-input"
                        placeholder="Enter street address"
                      />
                      {(errors.address as any)?.street && (
                        <p className="mt-1 text-sm text-red-600">{(errors.address as any).street.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">City *</label>
                        <input
                          type="text"
                          {...register('address.city' as any)}
                          className="form-input"
                          placeholder="Enter city"
                        />
                        {(errors.address as any)?.city && (
                          <p className="mt-1 text-sm text-red-600">{(errors.address as any).city.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">State *</label>
                        <input
                          type="text"
                          {...register('address.state' as any)}
                          className="form-input"
                          placeholder="Enter state"
                        />
                        {(errors.address as any)?.state && (
                          <p className="mt-1 text-sm text-red-600">{(errors.address as any).state.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">Pincode *</label>
                        <input
                          type="text"
                          {...register('address.pincode' as any)}
                          className="form-input"
                          placeholder="123456"
                        />
                        {(errors.address as any)?.pincode && (
                          <p className="mt-1 text-sm text-red-600">{(errors.address as any).pincode.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Save className="h-4 w-4" />
                    <span>{saving ? 'Saving...' : editingClient ? 'Update' : 'Create'}</span>
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Clients List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        {clients.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No clients found</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary mt-4 inline-flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add your first client</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Client Name</th>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Location</th>
                  <th className="table-header">GSTIN</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clients.map((client) => (
                  <tr key={client._id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">
                      {client.clientName}
                    </td>
                    <td className="table-cell">{client.contactNumber}</td>
                    <td className="table-cell">{client.email}</td>
                    <td className="table-cell">
                      {client.address.city}, {client.address.state}
                    </td>
                    <td className="table-cell">
                      <div className="flex flex-col">
                        <span>{client.gstin || '-'}</span>
                        {client.gstin && client.taxType && (
                          <span className={`text-xs mt-1 px-2 py-0.5 rounded-full inline-block w-fit ${
                            client.taxType === 'IGST' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {client.taxType === 'IGST' ? 'IGST' : 'SGST+CGST'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(client)}
                          className="text-primary-600 hover:text-primary-700"
                          title="Edit client"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(client)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete client"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingClient && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Client</h3>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingClient(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
                <div>
                  <p className="text-gray-900 font-medium">Are you sure you want to delete this client?</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone. This will permanently delete client <strong>{deletingClient.clientName}</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingClient(null);
                }}
                disabled={deleting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-primary bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Delete Client'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Clients;
